import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import {
  ChevronDown,
  ChevronRight,
  Plus,
  Settings,
  SlidersHorizontal,
  Trash2,
  Upload,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { fetchSwaggerEndpoints } from "../../../helpers/swaggerHelper";
import {
  Library,
  LookupModel,
  RequestType,
} from "../../../models/workFlow/ExecutionFlowModel";
import { fetchRequestResponse } from "../../../services/AdminServices/ConnectionService";

import { RequestResponseDTO } from "../../../models/workFlow/ConnectionModel";

// Type definitions
interface Claim {
  id: number;
  name: string;
  apiEndpoint: string;
  parameterSource:
  | "claim"
  | "previous_output"
  | "system_variable"
  | "Search_parameter"
  | "selected_Mail_Attachments"
  | "manual"
  | "Lookup";
  parameterKey: string;
  parameterValue: string;
  requestGroup: string;
  isLoadingRequestResponse?: boolean;
  parameterMappings: ParameterMapping[];
  inputJson: string; // Add this
  outputJson: string; // Add this
}
interface ParameterMapping {
  id: number;
  source: string;
  target: string;
  sourceType:
  | "claim"
  | "previous_step"
  | "system_variable"
  | "Search_parameter"
  | "selected_Mail_Attachments"
  | "manual"
  | "Lookup";
  sourceStepId?: number; // Only for previous_step type
  lookupConfig?: string;
}
interface LookupConfiguration {
  endpoint: string;
  inputJson: string;
  outputJson: string;
  mappings: Array<{
    outputPath: string;
    valuePath: string;
  }>;
}

interface UploadAction {
  id: number;
  enabled: boolean;
  apiEndpoint: string;
  parameterMappings: ParameterMapping[];
  inputJson: string;
  outputJson: string;
}

interface FlowStep {
  id: number;
  apiEndpoint: string;
  parameters: Record<string, any>;
  parameterMappings: ParameterMapping[];
  condition: string;
  order: number;
  inputJson: string;
  outputJson: string;
  requestGroup: string;
  isLoadingRequestResponse?: boolean;
  uploadAction?: UploadAction; // Add this
  outputMappings?: OutputMapping[];
}

interface StepCardProps {
  step: FlowStep;
  stepNumber: number;
  swaggerEndpoints: RequestType[];
  onUpdateStep: (stepId: number, field: keyof FlowStep, value: any) => void;
  onUpdateParameter: (
    stepId: number,
    paramName: string,
    paramValue: string
  ) => void;
  onUpdateParameterMapping: (stepId: number, mapping: ParameterMapping) => void;
  onRemoveParameterMapping: (stepId: number, mappingId: number) => void;
  onAddParameterMapping: (stepId: number) => void;
  onRemoveStep: (stepId: number) => void;
  onRequestTypeSelect: (
    stepId: number,
    item: RequestType,
    requestGroup: string,
    isSwaggerEnabled: boolean,
    swaggerUrl: string
  ) => Promise<void>;
  claims: Claim[];
  previousSteps: FlowStep[];
  accessToken: string;
  conId: number;
  isSwaggerEnabled: boolean; // Add this prop
  swaggerUrl?: string;
}

interface GroupedEndpoints {
  [key: string]: RequestType[];
}

interface OutputMapping {
  outputPath: string;
  libraryField: keyof Library;
}

const StepCard: React.FC<StepCardProps> = ({
  step,
  stepNumber,
  swaggerEndpoints,
  onUpdateStep,
  onUpdateParameter,
  onUpdateParameterMapping,
  onRemoveParameterMapping,
  onAddParameterMapping,
  onRemoveStep,
  onRequestTypeSelect,
  claims,
  previousSteps,
  accessToken,
  conId,
  isSwaggerEnabled,
  swaggerUrl,
}) => {
  const {
    isOpen: isLookupModalOpen,
    onOpen: onLookupModalOpen,
    onClose: onLookupModalClose,
  } = useDisclosure();
  const [currentLookupMappingId, setCurrentLookupMappingId] = useState<
    number | null
  >(null);
  const [lookupConfig, setLookupConfig] = useState<LookupConfiguration>({
    endpoint: "",
    inputJson: "",
    outputJson: "",
    mappings: [],
  });
  const [newLookupMapping, setNewLookupMapping] = useState({
    outputPath: "",
    valuePath: "",
  });

  const selectedEndpoint = swaggerEndpoints.find(
    (endpoint) => endpoint.name === step.apiEndpoint
  );
  const [showAdvanced, setShowAdvanced] = useState(true);
  const [endpoints, setEndpoints] = useState<RequestType[]>(swaggerEndpoints);
  const [manualInputJson, setManualInputJson] = useState(step.inputJson || "");
  const [manualOutputJson, setManualOutputJson] = useState(
    step.outputJson || ""
  );
  const [manualEndpointName, setManualEndpointName] = useState(
    step.apiEndpoint || ""
  );
  const [manualRequestGroup, setManualRequestGroup] = useState(
    step.requestGroup || ""
  ); // For manual request group

  // Fetch endpoints from Swagger if enabled
  useEffect(() => {
    if (isSwaggerEnabled && swaggerUrl) {
      fetchSwaggerEndpoints(swaggerUrl).then((data) => {
        setEndpoints(data);
      });
    }
  }, [isSwaggerEnabled, swaggerUrl]);

  // Group endpoints by type
  const groupedEndpoints: GroupedEndpoints = swaggerEndpoints.reduce(
    (acc, endpoint) => {
      const group = endpoint.type || "Uncategorized";
      if (!acc[group]) {
        acc[group] = [];
      }
      acc[group].push(endpoint);
      return acc;
    },
    {} as GroupedEndpoints
  );

  const handleEndpointChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ): void => {
    const selectedEndpointName = e.target.value;
    const selectedEndpoint = swaggerEndpoints.find(
      (ep) => ep.name === selectedEndpointName
    );
    if (selectedEndpoint) {
      onRequestTypeSelect(
        step.id,
        selectedEndpoint,
        selectedEndpoint.type || "",
        isSwaggerEnabled,
        swaggerUrl || ""
      );
    }
  };

  const handleMappingChange = (
    mappingId: number,
    field: keyof ParameterMapping,
    value: any
  ) => {
    const mapping = step.parameterMappings.find((m) => m.id === mappingId);
    if (mapping) {
      const updatedMapping = { ...mapping, [field]: value };

      // Automatically set sourceType based on the source value
      if (field === "source") {
        if (value === "searching") {
          updatedMapping.sourceType = "Search_parameter"; // Explicitly set for search
        } else if (value.startsWith("claim.")) {
          updatedMapping.sourceType = "claim";
        } else if (value.startsWith("step")) {
          updatedMapping.sourceType = "previous_step";
          const stepNumber = parseInt(value.split(".")[0].replace("step", ""));
          updatedMapping.sourceStepId = previousSteps[stepNumber - 1]?.id;
        } else if (value.startsWith("user.")) {
          updatedMapping.sourceType = "system_variable";
        } else if (value.startsWith("pagination_")) {
          updatedMapping.sourceType = "system_variable";
        }
      }

      onUpdateParameterMapping(step.id, updatedMapping);
    }
  };

  const handleUploadActionMappingChange = (
    mappingId: number,
    field: keyof ParameterMapping,
    value: any
  ) => {

    if (!step.uploadAction) return;

    const mapping = step.uploadAction.parameterMappings.find(
      (m) => m.id === mappingId
    );
    if (mapping) {
      const updatedMapping = { ...mapping, [field]: value };

      // Automatically set sourceType based on the source value
      if (field === "source") {
        if (value === "selectedMailAttachments") {
          updatedMapping.sourceType = "selected_Mail_Attachments";
        } else if (value === "lookup") {
          updatedMapping.sourceType = "Lookup";
        } else if (value === "manual") {
          updatedMapping.sourceType = "manual";
        } else if (value.startsWith("claim.")) {
          updatedMapping.sourceType = "claim";
        } else if (value.startsWith("step")) {
          updatedMapping.sourceType = "previous_step";
          const stepNumber = parseInt(value.split(".")[0].replace("step", ""));

          // Check if it's the current step
          if (stepNumber === previousSteps.length + 1) {
            // Current step
            updatedMapping.sourceStepId = step.id;
          } else {
            // Previous step
            updatedMapping.sourceStepId = previousSteps[stepNumber - 1]?.id;
          }
        } else if (value.startsWith("user.")) {
          updatedMapping.sourceType = "system_variable";
        }
      }

      const updatedMappings = step.uploadAction.parameterMappings.map((m) =>
        m.id === mappingId ? updatedMapping : m
      );

      onUpdateStep(step.id, "uploadAction", {
        ...step.uploadAction,
        parameterMappings: updatedMappings,
      });
    }
  };

  const handleAddLookupMapping = () => {
    if (newLookupMapping.outputPath && newLookupMapping.valuePath) {
      setLookupConfig({
        ...lookupConfig,
        mappings: [...lookupConfig.mappings, newLookupMapping],
      });
      setNewLookupMapping({
        outputPath: "",

        valuePath: "",
      });
    }
  };

  const handleRemoveLookupMapping = (index: number) => {
    setLookupConfig({
      ...lookupConfig,
      mappings: lookupConfig.mappings.filter((_, i) => i !== index),
    });
  };

  const saveLookupConfiguration = () => {
    if (!step.uploadAction || currentLookupMappingId === null) return;

    // Update the mapping with the lookup configuration
    const updatedMappings = step.uploadAction.parameterMappings.map((m) => {
      if (m.id === currentLookupMappingId) {
        return {
          ...m,
          lookupConfig: JSON.stringify(lookupConfig),
        };
      }
      return m;
    });

    onUpdateStep(step.id, "uploadAction", {
      ...step.uploadAction,
      parameterMappings: updatedMappings,
    });

    onLookupModalClose();
  };

  // Get available output paths for lookup configuration
  const getLookupOutputPaths = (): Array<{
    value: string;
    label: string;
  }> => {
    const paths: Array<{ value: string; label: string }> = [];

    try {
      const outputJson = lookupConfig.outputJson
        ? JSON.parse(lookupConfig.outputJson)
        : {};

      const processObject = (obj: any, prefix: string = "") => {
        Object.entries(obj).forEach(([key, value]) => {
          const currentPath = prefix ? `${prefix}.${key}` : key;

          if (Array.isArray(value)) {
            paths.push({
              value: currentPath,
              label: currentPath,
            });

            if (value.length > 0 && typeof value[0] === "object") {
              processObject(value[0], `${key}[0]`);
            }
          } else if (typeof value === "object" && value !== null) {
            paths.push({
              value: currentPath,
              label: currentPath,
            });
            processObject(value, currentPath);
          } else {
            paths.push({
              value: currentPath,
              label: currentPath,
            });
          }
        });
      };

      if (Array.isArray(outputJson)) {
        if (outputJson.length > 0 && typeof outputJson[0] === "object") {
          processObject(outputJson[0], "");
        }
      } else {
        processObject(outputJson);
      }
    } catch (e) {
      console.error("Error parsing lookup output JSON", e);
    }

    return paths;
  };

  const parseOutputJson = (jsonStr: string): Record<string, any> | null => {
    if (!jsonStr) return null;

    try {
      const parsed = JSON.parse(jsonStr);
      const result: Record<string, any> = {};

      const processObject = (obj: any, prefix: string) => {
        Object.entries(obj).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            const arrayPrefix = `${prefix}${key}`;
            result[`${arrayPrefix}`] = "list";
            result[`${arrayPrefix}.count`] = "number";
            const firstItem = value[0];
            if (typeof firstItem === "object" && firstItem !== null) {
              Object.entries(firstItem).forEach(([k, v]) => {
                result[`${arrayPrefix}.${k}`] = typeof v;
              });
            }
          } else if (typeof value === "object" && value !== null) {
            processObject(value, `${prefix}${key}.`);
          } else {
            result[`${prefix}${key}`] = typeof value;
          }
        });
      };

      if (typeof parsed === "object" && parsed !== null) {
        if (Array.isArray(parsed)) {
          result["items"] = "list";
          result["items.count"] = "number";
          const first = parsed[0];
          if (typeof first === "object" && first !== null) {
            Object.entries(first).forEach(([key, value]) => {
              result[`items.${key}`] = typeof value;
            });
          }
        } else {
          processObject(parsed, "");
        }
        return result;
      }

      return null;
    } catch (error) {
      console.error("Failed to parse output JSON:", error);
      return null;
    }
  };

  const convertUploadInputJson = (inputJsonStr: string): string => {
    if (!inputJsonStr) return inputJsonStr;

    try {
      const parsed = JSON.parse(inputJsonStr);

      // Check if it's an object (not an array) and has properties
      if (
        typeof parsed === "object" &&
        !Array.isArray(parsed) &&
        parsed !== null
      ) {
        const keys = Object.keys(parsed);

        // If there's exactly one key and its value is an array
        if (keys.length === 1) {
          const firstKey = keys[0];
          const firstValue = parsed[firstKey];

          if (Array.isArray(firstValue)) {
            // Return the array directly as JSON string
            return JSON.stringify(firstValue, null, 2);
          }
        }
      }

      // If no conversion needed, return original
      return inputJsonStr;
    } catch (error) {
      console.error("Error parsing upload input JSON for conversion:", error);
      // If parsing fails, return original
      return inputJsonStr;
    }
  };

  const getAvailableSources = (
    includeCurrentStep: boolean = false,
    currentStepNumber: number = stepNumber
  ): Array<{
    value: string;
    label: string;
    type:
    | "claim"
    | "previous_step"
    | "system_variable"
    | "manual"
    | "Search_parameter"
    | "selected_Mail_Attachments"
    | "Lookup";
    details?: string;
  }> => {
    const sources: Array<{
      value: string;
      label: string;
      type:
      | "claim"
      | "previous_step"
      | "system_variable"
      | "manual"
      | "Search_parameter"
      | "selected_Mail_Attachments"
      | "Lookup";
      details?: string;
    }> = [];

    // Add claims as sources

    console.log("-======= getAvailableSources claims ---------- ", claims)

    claims
      .filter(c => c.parameterSource === "claim" && c.outputJson)
      .forEach((claim) => {
        try {
          const claimValue = JSON.parse(claim.outputJson);
          Object.keys(claimValue).forEach((key) => {
            sources.push({
              value: `claim.${key}`,
              label: `Claim: ${claim.name}`,
              type: "claim",
              details: key,
            });
          });
        } catch (e) {
          console.error("Error parsing claim outputJson", e);
        }
      });

    //   claims.forEach((claim) => {
    //   try {
    //     const claimValue = claim.parameterValue
    //       ? JSON.parse(claim.parameterValue)
    //       : {};
    //     Object.keys(claimValue).forEach((key) => {
    //       sources.push({
    //         value: `claim.${key}`,
    //         label: `Claim: ${claim.name}`,
    //         type: "claim",
    //         details: key,
    //       });
    //     });
    //   } catch (e) {
    //     console.error("Error parsing claim value", e);
    //   }
    // });


    // Add previous steps as sources
    previousSteps.forEach((prevStep, idx) => {
      try {
        const outputSchema = parseOutputJson(prevStep.outputJson);
        if (outputSchema) {
          Object.entries(outputSchema).forEach(([path, type]) => {
            if (!path.endsWith(".count")) {
              sources.push({
                value: `step${idx + 1}.${path}`,
                label: `Step ${idx + 1}: ${prevStep.apiEndpoint || "Previous Step"
                  }`,
                type: "previous_step",
                details: `${path} (${type})`,
              });
            }
          });
        }
      } catch (e) {
        console.error("Error parsing previous step output", e);
      }
    });

    if (includeCurrentStep && step.outputJson) {
      try {
        const outputSchema = parseOutputJson(step.outputJson);
        if (outputSchema) {
          Object.entries(outputSchema).forEach(([path, type]) => {
            if (!path.endsWith(".count")) {
              sources.push({
                value: `step${currentStepNumber}.${path}`,
                label: `Step ${currentStepNumber}: ${step.apiEndpoint || "Current Step"
                  }`,
                type: "previous_step",
                details: `${path} (${type})`,
              });
            }
          });
        }
      } catch (e) {
        console.error("Error parsing current step output", e);
      }
    }

    // Add system variables
    const systemVars = [
      { key: "user.id", label: "User ID" },
      { key: "user.mail", label: "User Email" },
      { key: "user.tenantId", label: "Tenant ID" },
    ];

    systemVars.forEach((variable) => {
      sources.push({
        value: variable.key,
        label: "System Variable",
        type: "system_variable",
        details: variable.label,
      });
    });

    // Add static and search parameters
    sources.push(
      {
        value: "manual",
        label: "Manual Input",
        type: "manual",
        details: "Manually entered value",
      },
      {
        value: "selectedMailAttachments",
        label: "Selected Mail Attachments",
        type: "selected_Mail_Attachments",
        details: "Mail attachments selected by the user",
      },
      {
        value: "lookup",
        label: "Lookup",
        type: "Lookup",
        details: "Add Dropdown for specific parameter",
      },
      {
        value: "pagination_parameter_limit",
        label: "Pagination",
        type: "system_variable",
        details: "Limit parameter",
      },
      {
        value: "pagination_parameter_offset",
        label: "Pagination",
        type: "system_variable",
        details: "Offset parameter",
      },
      {
        value: "searching",
        label: "Search",
        type: "Search_parameter",
        details: "Search parameter",
      }
    );

    return sources;
  };

  const getAvailableTargets = (): Array<{
    value: string;
    label: string;
    details?: string;
  }> => {
    const targets: Array<{ value: string; label: string; details?: string }> =
      [];

    try {
      const inputJson = step.inputJson ? JSON.parse(step.inputJson) : {};
      Object.keys(inputJson).forEach((key) => {
        targets.push({
          value: key,
          label: "Input Parameter",
          details: key,
        });
      });
    } catch (e) {
      console.error("Error parsing input JSON", e);
    }

    return targets;
  };

  const getUploadActionTargets = (): Array<{
    value: string;
    label: string;
    details?: string;
  }> => {
    const targets: Array<{ value: string; label: string; details?: string }> =
      [];

    try {
      const inputJsonStr = step.uploadAction?.inputJson || "";

      // Convert the input JSON if needed
      const convertedInputJson = convertUploadInputJson(inputJsonStr);

      const parsedSchema = parseOutputJson(convertedInputJson);

      console.log("Upload Input Original JSON:", inputJsonStr);
      console.log("Upload Input Converted JSON:", convertedInputJson);
      console.log("Upload Input Parsed Schema:", parsedSchema);

      if (parsedSchema) {
        Object.entries(parsedSchema).forEach(([path, type]) => {
          // You can skip certain keys if needed, e.g., `.count` like in your original logic
          if (!path.endsWith(".count")) {
            targets.push({
              value: path,
              label: "Input Parameter",
              details: `${path} (${type})`,
            });
          }
        });
      }
    } catch (e) {
      console.error("Error parsing upload action input JSON", e);
    }

    return targets;
  };

  const handleAddUploadAction = () => {
    const uploadEndpoint = swaggerEndpoints.find((ep) =>
      ep.type?.toLowerCase().includes("upload")
    );

    onUpdateStep(step.id, "uploadAction", {
      id: Date.now(),
      enabled: true,
      apiEndpoint: uploadEndpoint?.name || "",
      parameterMappings: [],
      inputJson: "",
      outputJson: "",
    });
  };

  const handleRemoveUploadAction = () => {
    onUpdateStep(step.id, "uploadAction", undefined);
  };

  const handleUploadEndpointChange = async (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    if (!step.uploadAction) return;

    const selectedEndpointName = e.target.value;
    const selectedEndpoint = swaggerEndpoints.find(
      (ep) => ep.name === selectedEndpointName
    );

    if (selectedEndpoint && accessToken && conId) {
      try {
        const payload: RequestResponseDTO = {
          connectionId: conId,
          requestType: selectedEndpoint.name,
          requestGroup: selectedEndpoint.type || "",
        };

        const response = await fetchRequestResponse(accessToken, payload);

        onUpdateStep(step.id, "uploadAction", {
          ...step.uploadAction,
          apiEndpoint: selectedEndpoint.name,
          inputJson: response.inputJson || "",
          outputJson: response.outputJson || "",
          parameterMappings: [],
        });
      } catch (error) {
        console.error("Error fetching upload endpoint data:", error);
      }
    }
  };
  const handleAddUploadParameterMapping = () => {
    if (!step.uploadAction) return;

    const newMapping: ParameterMapping = {
      id: Date.now(),
      source: "",
      target: "",
      sourceType: "claim",
    };

    onUpdateStep(step.id, "uploadAction", {
      ...step.uploadAction,
      parameterMappings: [...step.uploadAction.parameterMappings, newMapping],
    });
  };

  const handleRemoveUploadParameterMapping = (mappingId: number) => {
    if (!step.uploadAction) return;

    onUpdateStep(step.id, "uploadAction", {
      ...step.uploadAction,
      parameterMappings: step.uploadAction.parameterMappings.filter(
        (m) => m.id !== mappingId
      ),
    });
  };

  const [outputMappings, setOutputMappings] = useState<OutputMapping[]>(
    step.outputMappings || []
  );
  const [selectedOutputPath, setSelectedOutputPath] = useState<string>("");
  const [selectedLibraryField, setSelectedLibraryField] =
    useState<keyof Library>("Title");

  // Get all available output paths from the step's output JSON
  const getAvailableOutputPaths = (): Array<{
    value: string;
    label: string;
    type: string;
  }> => {
    const paths: Array<{ value: string; label: string; type: string }> = [];

    try {
      const outputJson = step.outputJson ? JSON.parse(step.outputJson) : {};

      const processObject = (obj: any, prefix: string = "") => {
        Object.entries(obj).forEach(([key, value]) => {
          // Remove array index prefixes (like "0.") from the display label
          const displayKey = prefix.endsWith(".")
            ? key
            : `${prefix ? `${prefix}.` : ""}${key}`;
          const currentPath = prefix ? `${prefix}.${key}` : key;

          if (Array.isArray(value)) {
            paths.push({
              value: currentPath,
              label: displayKey, // Use displayKey without array indices
              type: "array",
            });

            if (value.length > 0 && typeof value[0] === "object") {
              processObject(value[0], `${key}[0]`);
            }
          } else if (typeof value === "object" && value !== null) {
            paths.push({
              value: currentPath,
              label: displayKey,
              type: "object",
            });
            processObject(value, currentPath);
          } else {
            paths.push({
              value: currentPath,
              label: displayKey,
              type: typeof value,
            });
          }
        });
      };

      // Special handling for array root
      if (Array.isArray(outputJson)) {
        if (outputJson.length > 0 && typeof outputJson[0] === "object") {
          processObject(outputJson[0], "");
        }
      } else {
        processObject(outputJson);
      }
    } catch (e) {
      console.error("Error parsing output JSON", e);
    }

    return paths;
  };

  // Library field options
  const libraryFields: Array<{ value: keyof Library; label: string }> = [
    { value: "Type", label: "Type" },
    { value: "Source", label: "Source" },
    { value: "Title", label: "Title" },
    { value: "Url", label: "Url" },
    { value: "DisplayName", label: "Display Name" },
  ];

  const lookupfiled: Array<{ value: keyof LookupModel; label: string }> = [
    { value: "Name", label: "Name" },
    { value: "Value", label: "Value" },
  ];

  // Add a new output mapping
  const addOutputMapping = () => {
    if (selectedOutputPath && selectedLibraryField) {
      const newMapping: OutputMapping = {
        outputPath: selectedOutputPath,
        libraryField: selectedLibraryField,
      };

      // Update the step's outputMappings array
      onUpdateStep(step.id, "outputMappings", [
        ...(step.outputMappings || []),
        newMapping,
      ]);

      // Also update local state for UI
      setOutputMappings([...outputMappings, newMapping]);
      setSelectedOutputPath("");
    }
  };

  // Remove an output mapping
  const removeOutputMapping = (index: number) => {
    setOutputMappings(outputMappings.filter((_, i) => i !== index));
  };

  const handleManualInputChange = (
    field: "inputJson" | "outputJson" | "apiEndpoint" | "requestGroup",
    value: string
  ) => {
    if (field === "inputJson") {
      setManualInputJson(value);
      onUpdateStep(step.id, "inputJson", value);
    } else if (field === "outputJson") {
      setManualOutputJson(value);
      onUpdateStep(step.id, "outputJson", value);
    } else if (field === "apiEndpoint") {
      setManualEndpointName(value);
      onUpdateStep(step.id, "apiEndpoint", value);
    } else if (field === "requestGroup") {
      setManualRequestGroup(value);
      onUpdateStep(step.id, "requestGroup", value); // Store the selected request group
    }
  };

  return (
    <Card>
      <CardBody>
        <VStack spacing={4} align="stretch">
          {/* Step Header */}
          <Flex justify="space-between" align="center">
            <HStack spacing={3}>
              <Badge
                colorScheme="blue"
                fontSize="sm"
                px={3}
                py={1}
                borderRadius="full"
              >
                Step {stepNumber}
              </Badge>
              <Text fontWeight="600" color="gray.700">
                {selectedEndpoint
                  ? `${selectedEndpoint.name} (${selectedEndpoint.type})`
                  : "Configure Step"}
              </Text>
              {step.isLoadingRequestResponse && (
                <Spinner size="sm" color="blue.500" />
              )}
            </HStack>
            <HStack>
              <Tooltip label={showAdvanced ? "Hide Advanced" : "Show Advanced"}>
                <IconButton
                  icon={
                    showAdvanced ? (
                      <ChevronDown size={16} />
                    ) : (
                      <ChevronRight size={16} />
                    )
                  }
                  size="sm"
                  variant="ghost"
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  aria-label={showAdvanced ? "Hide Advanced" : "Show Advanced"}
                />
              </Tooltip>
              <Tooltip label="Remove Step">
                <IconButton
                  icon={<Trash2 size={16} />}
                  size="sm"
                  colorScheme="red"
                  variant="ghost"
                  onClick={() => onRemoveStep(step.id)}
                  aria-label="Remove Step"
                />
              </Tooltip>
            </HStack>
          </Flex>

          {/* Configuration */}
          <HStack spacing={4} align="start">
            {/* API Endpoint Selection */}
            <FormControl>
              <FormLabel fontSize="sm" color="gray.600">
                <HStack spacing={2}>
                  <Settings size={14} />
                  <Text>API Endpoint</Text>
                </HStack>
              </FormLabel>
              {isSwaggerEnabled && swaggerUrl ? (
                <Select
                  placeholder="Select API endpoint..."
                  value={step.apiEndpoint}
                  // onChange={(e) =>
                  //   onUpdateStep(step.id, "apiEndpoint", e.target.value)
                  // }
                  onChange={handleEndpointChange}
                  bg="white"
                  size="sm"
                >
                  {swaggerEndpoints.map((endpoint) => (
                    <option key={endpoint.name} value={endpoint.name}>
                      {endpoint.name}
                    </option>
                  ))}
                </Select>
              ) : (
                <Input
                  placeholder="Enter API endpoint..."
                  value={manualEndpointName}
                  onChange={(e) =>
                    handleManualInputChange("apiEndpoint", e.target.value)
                  }
                  size="sm"
                  bg="white"
                />
              )}
            </FormControl>
          </HStack>

          {/* API Details */}
          {selectedEndpoint && (
            <Box bg="gray.50" p={3} borderRadius="md">
              <VStack align="stretch" spacing={3}>
                <HStack spacing={4}>
                  <Badge colorScheme="green" size="sm">
                    {selectedEndpoint.type}
                  </Badge>
                  <Text fontSize="sm" fontFamily="mono" color="gray.600">
                    {selectedEndpoint.name}
                  </Text>
                  {step.requestGroup && (
                    <Badge colorScheme="purple" size="sm">
                      {step.requestGroup}
                    </Badge>
                  )}
                </HStack>

                {/* Input JSON Display */}
                {step.inputJson && (
                  <Box>
                    <Text
                      fontSize="sm"
                      fontWeight="600"
                      color="gray.700"
                      mb={2}
                    >
                      Input JSON:
                    </Text>
                    <Box
                      as="pre"
                      bg="white"
                      p={2}
                      borderRadius="md"
                      fontSize="xs"
                      overflow="auto"
                      maxH="150px"
                      border="1px solid"
                      borderColor="gray.200"
                      whiteSpace="pre-wrap"
                    >
                      {typeof step.inputJson === "string"
                        ? step.inputJson
                        : JSON.stringify(step.inputJson, null, 2)}
                    </Box>
                  </Box>
                )}

                {/* Output JSON Display */}
                {step.outputJson && (
                  <Box>
                    <Text
                      fontSize="sm"
                      fontWeight="600"
                      color="gray.700"
                      mb={2}
                    >
                      Output JSON:
                    </Text>
                    <Box
                      as="pre"
                      bg="white"
                      p={2}
                      borderRadius="md"
                      fontSize="xs"
                      overflow="auto"
                      maxH="150px"
                      border="1px solid"
                      borderColor="gray.200"
                      whiteSpace="pre-wrap"
                    >
                      {typeof step.outputJson === "string"
                        ? step.outputJson
                        : JSON.stringify(step.outputJson, null, 2)}
                    </Box>

                    {/* Output to Library Mapping */}
                    <Box mt={4}>
                      <Text
                        fontSize="sm"
                        fontWeight="600"
                        color="gray.700"
                        mb={2}
                      >
                        Map Output to Library:
                      </Text>

                      <HStack spacing={3} mb={3}>
                        <Select
                          placeholder="Select output path"
                          value={selectedOutputPath}
                          onChange={(e) =>
                            setSelectedOutputPath(e.target.value)
                          }
                          size="sm"
                          bg="white"
                        >
                          {getAvailableOutputPaths().map((path) => (
                            <option key={path.value} value={path.value}>
                              {path.label}
                            </option>
                          ))}
                        </Select>

                        <Select
                          value={selectedLibraryField}
                          onChange={(e) =>
                            setSelectedLibraryField(
                              e.target.value as keyof Library
                            )
                          }
                          size="sm"
                          bg="white"
                        >
                          {libraryFields.map((field) => (
                            <option key={field.value} value={field.value}>
                              {field.label}
                            </option>
                          ))}
                        </Select>

                        <Button
                          size="sm"
                          colorScheme="blue"
                          onClick={addOutputMapping}
                          isDisabled={!selectedOutputPath}
                        >
                          Add
                        </Button>
                      </HStack>

                      {outputMappings.length > 0 ? (
                        <Table variant="simple" size="sm">
                          <Thead>
                            <Tr>
                              <Th>Output Path</Th>
                              <Th>Library Field</Th>
                              <Th>Actions</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {outputMappings.map((mapping, index) => (
                              <Tr
                                key={`${mapping.outputPath}-${mapping.libraryField}`}
                              >
                                <Td>{mapping.outputPath}</Td>
                                <Td>{mapping.libraryField}</Td>
                                <Td>
                                  <IconButton
                                    icon={<Trash2 size={14} />}
                                    size="sm"
                                    variant="ghost"
                                    colorScheme="red"
                                    onClick={() => removeOutputMapping(index)}
                                    aria-label="Remove mapping"
                                  />
                                </Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      ) : (
                        <Text
                          fontSize="sm"
                          color="gray.500"
                          textAlign="center"
                          py={2}
                        >
                          No output mappings defined yet
                        </Text>
                      )}
                    </Box>
                  </Box>
                )}
              </VStack>
            </Box>
          )}

          {/* Manual Input for Request and Response JSON */}
          {!isSwaggerEnabled && (
            <>
              <FormControl>
                <FormLabel fontSize="sm" color="gray.600">
                  Request Method
                </FormLabel>
                <Select
                  placeholder="Select request method..."
                  value={manualRequestGroup}
                  onChange={(e) =>
                    handleManualInputChange("requestGroup", e.target.value)
                  }
                  bg="white"
                  size="sm"
                >
                  <option value="GET">GET</option>
                  <option value="POST">POST</option>
                  <option value="PUT">PUT</option>
                  <option value="DELETE">DELETE</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm" color="gray.600">
                  Request JSON
                </FormLabel>
                <Input
                  placeholder="Enter request JSON..."
                  value={manualInputJson}
                  onChange={(e) =>
                    handleManualInputChange("inputJson", e.target.value)
                  }
                  size="sm"
                  bg="white"
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm" color="gray.600">
                  Response JSON
                </FormLabel>
                <Input
                  placeholder="Enter response JSON..."
                  value={manualOutputJson}
                  onChange={(e) =>
                    handleManualInputChange("outputJson", e.target.value)
                  }
                  size="sm"
                  bg="white"
                />
              </FormControl>
              {/* Output to Library Mapping */}
              <Box mt={4}>
                <Text fontSize="sm" fontWeight="600" color="gray.700" mb={2}>
                  Map Output to Library:
                </Text>

                <HStack spacing={3} mb={3}>
                  <Select
                    placeholder="Select output path"
                    value={selectedOutputPath}
                    onChange={(e) => setSelectedOutputPath(e.target.value)}
                    size="sm"
                    bg="white"
                  >
                    {getAvailableOutputPaths().map((path) => (
                      <option key={path.value} value={path.value}>
                        {path.label}
                      </option>
                    ))}
                  </Select>

                  <Select
                    value={selectedLibraryField}
                    onChange={(e) =>
                      setSelectedLibraryField(e.target.value as keyof Library)
                    }
                    size="sm"
                    bg="white"
                  >
                    {libraryFields.map((field) => (
                      <option key={field.value} value={field.value}>
                        {field.label}
                      </option>
                    ))}
                  </Select>

                  <Button
                    size="sm"
                    colorScheme="blue"
                    onClick={addOutputMapping}
                    isDisabled={!selectedOutputPath}
                  >
                    Add
                  </Button>
                </HStack>

                {outputMappings.length > 0 ? (
                  <Table variant="simple" size="sm">
                    <Thead>
                      <Tr>
                        <Th>Output Path</Th>
                        <Th>Library Field</Th>
                        <Th>Actions</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {outputMappings.map((mapping, index) => (
                        <Tr
                          key={`${mapping.outputPath}-${mapping.libraryField}`}
                        >
                          <Td>{mapping.outputPath}</Td>
                          <Td>{mapping.libraryField}</Td>
                          <Td>
                            <IconButton
                              icon={<Trash2 size={14} />}
                              size="sm"
                              variant="ghost"
                              colorScheme="red"
                              onClick={() => removeOutputMapping(index)}
                              aria-label="Remove mapping"
                            />
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                ) : (
                  <Text
                    fontSize="sm"
                    color="gray.500"
                    textAlign="center"
                    py={2}
                  >
                    No output mappings defined yet
                  </Text>
                )}
              </Box>
            </>
          )}

          {/* Upload Action Section */}
          <Box mt={4}>
            <Flex justify="space-between" align="center" mb={2}>
              <Flex align="center" gap={2}>
                <Upload size={16} color="#2563eb" /> {/* Blue upload icon */}
                <Text fontSize="sm" fontWeight="600" color="gray.700">
                  Upload Action
                </Text>
              </Flex>
              {step.uploadAction ? (
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="red"
                  onClick={handleRemoveUploadAction}
                >
                  Remove Upload
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="blue"
                  onClick={handleAddUploadAction}
                >
                  Add Upload Action
                </Button>
              )}
            </Flex>

            {step.uploadAction && (
              <Box bg="gray.100" p={3} borderRadius="md">
                <VStack spacing={3} align="stretch">
                  <FormControl>
                    <FormLabel fontSize="sm" color="gray.600">
                      Upload API Endpoint
                    </FormLabel>

                    {/* Swagger mode: endpoint select | Manual mode: free input */}
                    {isSwaggerEnabled && swaggerUrl ? (
                      <Select
                        placeholder="Select upload endpoint..."
                        value={step.uploadAction.apiEndpoint}
                        onChange={handleUploadEndpointChange}
                        bg="white"
                        size="sm"
                      >
                        {Object.entries(groupedEndpoints).map(
                          ([type, endpoints]) => (
                            <optgroup key={type} label={type}>
                              {endpoints
                                .filter((endpoint) =>
                                  endpoint.type?.toLowerCase()
                                )
                                .map((endpoint) => (
                                  <option
                                    key={endpoint.name}
                                    value={endpoint.name}
                                  >
                                    {endpoint.name}
                                  </option>
                                ))}
                            </optgroup>
                          )
                        )}
                      </Select>
                    ) : (
                      <Input
                        placeholder="Enter upload endpoint..."
                        value={step.uploadAction.apiEndpoint || ""}
                        onChange={(e) =>
                          onUpdateStep(step.id, "uploadAction", {
                            ...step.uploadAction,
                            apiEndpoint: e.target.value,
                          })
                        }
                        size="sm"
                        bg="white"
                      />
                    )}
                  </FormControl>

                  {/* Upload Input JSON */}
                  <FormControl>
                    <FormLabel fontSize="sm" color="gray.600">
                      Upload Input JSON
                    </FormLabel>

                    {isSwaggerEnabled ? (
                      step.uploadAction.inputJson ? (
                        <Box
                          as="pre"
                          bg="white"
                          p={2}
                          borderRadius="md"
                          fontSize="xs"
                          overflow="auto"
                          maxH="150px"
                          border="1px solid"
                          borderColor="gray.200"
                          whiteSpace="pre-wrap"
                        >
                          {(() => {
                            const originalJson =
                              typeof step.uploadAction!.inputJson === "string"
                                ? step.uploadAction!.inputJson
                                : JSON.stringify(
                                  step.uploadAction!.inputJson,
                                  null,
                                  2
                                );

                            try {
                              const parsed = JSON.parse(originalJson);
                              return JSON.stringify(parsed, null, 2);
                            } catch {
                              return originalJson;
                            }
                          })()}
                        </Box>
                      ) : (
                        <Text fontSize="sm" color="gray.500">
                          No upload input JSON available
                        </Text>
                      )
                    ) : (
                      <Textarea
                        placeholder="Enter upload input JSON..."
                        value={step.uploadAction.inputJson || ""}
                        onChange={(e) =>
                          onUpdateStep(step.id, "uploadAction", {
                            ...step.uploadAction,
                            inputJson: e.target.value,
                          })
                        }
                        size="sm"
                        bg="white"
                      />
                    )}
                  </FormControl>

                  {/* Upload Output JSON */}
                  <FormControl>
                    <FormLabel fontSize="sm" color="gray.600">
                      Upload Output JSON
                    </FormLabel>

                    {isSwaggerEnabled ? (
                      step.uploadAction.outputJson ? (
                        <Box
                          as="pre"
                          bg="white"
                          p={2}
                          borderRadius="md"
                          fontSize="xs"
                          overflow="auto"
                          maxH="150px"
                          border="1px solid"
                          borderColor="gray.200"
                          whiteSpace="pre-wrap"
                        >
                          {typeof step.uploadAction!.outputJson === "string"
                            ? step.uploadAction!.outputJson
                            : JSON.stringify(
                              step.uploadAction!.outputJson,
                              null,
                              2
                            )}
                        </Box>
                      ) : (
                        <Text fontSize="sm" color="gray.500">
                          No upload output JSON available
                        </Text>
                      )
                    ) : (
                      <Textarea
                        placeholder="Enter upload output JSON..."
                        value={step.uploadAction.outputJson || ""}
                        onChange={(e) =>
                          onUpdateStep(step.id, "uploadAction", {
                            ...step.uploadAction,
                            outputJson: e.target.value,
                          })
                        }
                        size="sm"
                        bg="white"
                      />
                    )}
                  </FormControl>

                  {/* Upload Parameter Mappings */}
                  <Box>
                    <Text
                      fontSize="sm"
                      fontWeight="600"
                      color="gray.700"
                      mb={2}
                    >
                      Upload Parameter Mappings:
                    </Text>

                    {step.uploadAction.parameterMappings.length > 0 ? (
                      <Table variant="simple" size="sm">
                        <Thead>
                          <Tr>
                            <Th>Source</Th>
                            <Th>Destination (Upload Input JSON)</Th>
                            <Th>Actions</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {step.uploadAction.parameterMappings.map(
                            (mapping) => (
                              <Tr key={mapping.id}>
                                <Td>
                                  <Select
                                    value={mapping.source}
                                    onChange={(e) =>
                                      handleUploadActionMappingChange(
                                        mapping.id,
                                        "source",
                                        e.target.value
                                      )
                                    }
                                    size="sm"
                                    bg="white"
                                  >
                                    <option value="">Select source...</option>

                                    {/* Claims */}
                                    <optgroup label="Claims">
                                      {getAvailableSources()
                                        .filter((s) => s.type === "claim")
                                        .map((source) => (
                                          <option
                                            key={source.value}
                                            value={source.value}
                                          >
                                            {source.label} - {source.details}
                                          </option>
                                        ))}
                                    </optgroup>

                                    {/* Previous Steps */}
                                    {previousSteps.length > 0 && (
                                      <optgroup label="Previous Steps">
                                        {getAvailableSources()
                                          .filter(
                                            (s) => s.type === "previous_step"
                                          )
                                          .map((source) => (
                                            <option
                                              key={source.value}
                                              value={source.value}
                                            >
                                              {source.label} - {source.details}
                                            </option>
                                          ))}
                                      </optgroup>
                                    )}

                                    {step.outputJson && (
                                      <optgroup
                                        label={`Step ${stepNumber} (Current)`}
                                      >
                                        {getAvailableSources(true)
                                          .filter((s) =>
                                            s.value.startsWith(
                                              `step${stepNumber}.`
                                            )
                                          )
                                          .map((source) => (
                                            <option
                                              key={source.value}
                                              value={source.value}
                                            >
                                              {source.label} -{" "}
                                              {source.details}
                                            </option>
                                          ))}
                                      </optgroup>
                                    )}

                                    {/* System Variable */}
                                    <optgroup label="System Variable">
                                      {getAvailableSources()
                                        .filter(
                                          (s) => s.type === "system_variable"
                                        )
                                        .map((source) => (
                                          <option
                                            key={source.value}
                                            value={source.value}
                                          >
                                            {source.label} - {source.details}
                                          </option>
                                        ))}
                                    </optgroup>

                                    {/* Search Parameters */}
                                    <optgroup label="Search Parameters">
                                      {getAvailableSources()
                                        .filter(
                                          (s) => s.type === "Search_parameter"
                                        )
                                        .map((source) => (
                                          <option
                                            key={source.value}
                                            value={source.value}
                                          >
                                            {source.label} - {source.details}
                                          </option>
                                        ))}
                                    </optgroup>

                                    {/* Mail Attachments */}
                                    <optgroup label="Mail Attachments">
                                      {getAvailableSources()
                                        .filter(
                                          (s) =>
                                            s.type ===
                                            "selected_Mail_Attachments"
                                        )
                                        .map((source) => (
                                          <option
                                            key={source.value}
                                            value={source.value}
                                          >
                                            {source.label} - {source.details}
                                          </option>
                                        ))}
                                    </optgroup>

                                    {/* Lookup */}
                                    <optgroup label="Lookup">
                                      {getAvailableSources()
                                        .filter((s) => s.type === "Lookup")
                                        .map((source) => (
                                          <option
                                            key={source.value}
                                            value={source.value}
                                          >
                                            {source.label} - {source.details}
                                          </option>
                                        ))}
                                    </optgroup>

                                    {/* manual input */}
                                    <optgroup label="Manual Input">
                                      {getAvailableSources()
                                        .filter(
                                          (s) =>
                                            s.type === "manual"
                                        )
                                        .map((source) => (
                                          <option
                                            key={source.value}
                                            value={source.value}
                                          >
                                            {source.label} - {source.details}
                                          </option>
                                        ))}
                                    </optgroup>
                                  </Select>
                                </Td>

                                <Td>
                                  <Select
                                    value={mapping.target}
                                    onChange={(e) =>
                                      handleUploadActionMappingChange(
                                        mapping.id,
                                        "target",
                                        e.target.value
                                      )
                                    }
                                    size="sm"
                                    bg="white"
                                  >
                                    <option value="">Select target...</option>
                                    {getUploadActionTargets().map((target) => (
                                      <option
                                        key={target.value}
                                        value={target.value}
                                      >
                                        {target.details}
                                      </option>
                                    ))}
                                  </Select>
                                </Td>

                                <Td>
                                  <IconButton
                                    icon={<Trash2 size={14} />}
                                    size="sm"
                                    variant="ghost"
                                    colorScheme="red"
                                    onClick={() =>
                                      handleRemoveUploadParameterMapping(
                                        mapping.id
                                      )
                                    }
                                    aria-label="Remove mapping"
                                    ml={2}
                                  />
                                  {mapping.sourceType === "Lookup" && (
                                    <IconButton
                                      icon={<SlidersHorizontal size={14} />}
                                      size="sm"
                                      variant="ghost"
                                      colorScheme="blue"
                                      onClick={() => {
                                        setCurrentLookupMappingId(mapping.id);
                                        if (mapping.lookupConfig) {
                                          try {
                                            setLookupConfig(
                                              JSON.parse(mapping.lookupConfig)
                                            );
                                          } catch {
                                            setLookupConfig({
                                              endpoint: "",
                                              inputJson: "",
                                              outputJson: "",
                                              mappings: [],
                                            });
                                          }
                                        } else {
                                          setLookupConfig({
                                            endpoint: "",
                                            inputJson: "",
                                            outputJson: "",
                                            mappings: [],
                                          });
                                        }
                                        onLookupModalOpen();
                                      }}
                                      aria-label="View Lookup"
                                      ml={2}
                                    />
                                  )}
                                </Td>
                              </Tr>
                            )
                          )}
                        </Tbody>
                      </Table>
                    ) : (
                      <Text
                        fontSize="sm"
                        color="gray.500"
                        textAlign="center"
                        py={2}
                      >
                        No upload parameter mappings defined yet
                      </Text>
                    )}

                    <Button
                      leftIcon={<Plus size={14} />}
                      size="sm"
                      variant="outline"
                      onClick={handleAddUploadParameterMapping}
                    >
                      Add Upload Mapping
                    </Button>
                  </Box>
                </VStack>
              </Box>
            )}
          </Box>

          {/* ------------ Lookup model  -----------------*/}
          {/* Lookup Modal (supports both Swagger and Manual modes) */}
          <Modal
            isOpen={isLookupModalOpen}
            onClose={onLookupModalClose}
            size="xl"
          >
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>Configure Lookup Endpoint</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <VStack spacing={4} align="stretch">
                  <FormControl>
                    <FormLabel>Lookup Endpoint</FormLabel>

                    {/* Swagger: select + fetch | Manual: free input + editable JSON */}
                    {isSwaggerEnabled && swaggerUrl ? (
                      <Select
                        placeholder="Select lookup endpoint..."
                        value={lookupConfig.endpoint}
                        onChange={async (e) => {
                          const endpointName = e.target.value;
                          const selectedEndpointObj = swaggerEndpoints.find(
                            (ep) => ep.name === endpointName
                          );

                          // reset while loading
                          setLookupConfig({
                            ...lookupConfig,
                            endpoint: endpointName,
                            inputJson: "",
                            outputJson: "",
                            mappings: [],
                          });

                          if (selectedEndpointObj && accessToken && conId) {
                            try {
                              const payload: RequestResponseDTO = {
                                connectionId: conId,
                                requestType: selectedEndpointObj.name,
                                requestGroup: selectedEndpointObj.type,
                              };

                              const response = await fetchRequestResponse(
                                accessToken,
                                payload
                              );

                              setLookupConfig({
                                ...lookupConfig,
                                endpoint: endpointName,
                                inputJson: response.inputJson || "",
                                outputJson: response.outputJson || "",
                                mappings: [],
                              });
                            } catch (error) {
                              console.error(
                                "Error fetching lookup endpoint data:",
                                error
                              );
                            }
                          }
                        }}
                        bg="white"
                        size="sm"
                      >
                        {swaggerEndpoints.map((endpoint) => (
                          <option key={endpoint.name} value={endpoint.name}>
                            {endpoint.name} ({endpoint.type})
                          </option>
                        ))}
                      </Select>
                    ) : (
                      <>
                        <Input
                          placeholder="Enter lookup endpoint name..."
                          value={lookupConfig.endpoint || ""}
                          onChange={(e) =>
                            setLookupConfig({
                              ...lookupConfig,
                              endpoint: e.target.value,
                            })
                          }
                          bg="white"
                          size="sm"
                        />

                        <FormControl mt={2}>
                          <FormLabel fontSize="sm">Lookup Input JSON</FormLabel>
                          <Textarea
                            placeholder="Paste lookup input JSON..."
                            value={lookupConfig.inputJson || ""}
                            onChange={(e) =>
                              setLookupConfig({
                                ...lookupConfig,
                                inputJson: e.target.value,
                              })
                            }
                            size="sm"
                            bg="white"
                          />
                        </FormControl>

                        <FormControl mt={2}>
                          <FormLabel fontSize="sm">
                            Lookup Output JSON
                          </FormLabel>
                          <Textarea
                            placeholder="Paste lookup output JSON..."
                            value={lookupConfig.outputJson || ""}
                            onChange={(e) =>
                              setLookupConfig({
                                ...lookupConfig,
                                outputJson: e.target.value,
                              })
                            }
                            size="sm"
                            bg="white"
                          />
                        </FormControl>
                      </>
                    )}
                  </FormControl>

                  {lookupConfig.endpoint && (
                    <>
                      {/* Show preview when available (Swagger mode uses pre, Manual uses textarea above) */}
                      {isSwaggerEnabled && lookupConfig.inputJson && (
                        <Box>
                          <Text fontWeight="600" mb={2}>
                            Input JSON:
                          </Text>
                          <Box
                            as="pre"
                            bg="white"
                            p={2}
                            borderRadius="md"
                            fontSize="xs"
                            overflow="auto"
                            maxH="150px"
                            border="1px solid"
                            borderColor="gray.200"
                            whiteSpace="pre-wrap"
                          >
                            {lookupConfig.inputJson}
                          </Box>
                        </Box>
                      )}

                      {isSwaggerEnabled && lookupConfig.outputJson && (
                        <Box>
                          <Text fontWeight="600" mb={2}>
                            Output JSON:
                          </Text>
                          <Box
                            as="pre"
                            bg="white"
                            p={2}
                            borderRadius="md"
                            fontSize="xs"
                            overflow="auto"
                            maxH="150px"
                            border="1px solid"
                            borderColor="gray.200"
                            whiteSpace="pre-wrap"
                          >
                            {lookupConfig.outputJson}
                          </Box>
                        </Box>
                      )}

                      {/* Field Mappings */}
                      <Box>
                        <Text fontWeight="600" mb={2}>
                          Field Mappings:
                        </Text>
                        <Table variant="simple" size="sm">
                          <Thead>
                            <Tr>
                              <Th>Output Path</Th>
                              <Th>Lookup Value</Th>
                              <Th>Actions</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {lookupConfig.mappings.map((mapping, index) => (
                              <Tr key={index}>
                                <Td>{mapping.outputPath}</Td>
                                <Td>{mapping.valuePath}</Td>
                                <Td>
                                  <IconButton
                                    icon={<Trash2 size={14} />}
                                    size="sm"
                                    variant="ghost"
                                    colorScheme="red"
                                    onClick={() =>
                                      handleRemoveLookupMapping(index)
                                    }
                                    aria-label="Remove mapping"
                                  />
                                </Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>

                        <HStack mt={4} spacing={3}>
                          <Select
                            placeholder="Select output path"
                            value={newLookupMapping.outputPath}
                            onChange={(e) =>
                              setNewLookupMapping({
                                ...newLookupMapping,
                                outputPath: e.target.value,
                              })
                            }
                            size="sm"
                            bg="white"
                          >
                            {getLookupOutputPaths().map((path) => (
                              <option key={path.value} value={path.value}>
                                {path.label}
                              </option>
                            ))}
                          </Select>

                          <Select
                            placeholder="Select value path"
                            value={newLookupMapping.valuePath}
                            onChange={(e) =>
                              setNewLookupMapping({
                                ...newLookupMapping,
                                valuePath: e.target.value,
                              })
                            }
                            size="sm"
                            bg="white"
                          >
                            {lookupfiled.map((field) => (
                              <option key={field.value} value={field.value}>
                                {field.label}
                              </option>
                            ))}
                          </Select>

                          <Button
                            size="sm"
                            colorScheme="blue"
                            onClick={handleAddLookupMapping}
                            isDisabled={
                              !newLookupMapping.outputPath ||
                              !newLookupMapping.valuePath
                            }
                          >
                            Add
                          </Button>
                        </HStack>
                      </Box>
                    </>
                  )}
                </VStack>
              </ModalBody>

              <ModalFooter>
                <Button variant="outline" mr={3} onClick={onLookupModalClose}>
                  Cancel
                </Button>
                <Button colorScheme="blue" onClick={saveLookupConfiguration}>
                  Save Configuration
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>

          {/* ---------------------------------------------*/}
          {/* Advanced Configuration */}
          {showAdvanced && (
            <>
              {/* Parameter Mappings */}
              <Box>
                <Badge colorScheme="blue" fontSize="sm" fontWeight="600" mb={2} px={2} py={1} borderRadius="md">
                  Parameter Mappings:
                </Badge>


                {step.parameterMappings.length > 0 ? (
                  <Table variant="simple" size="sm">
                    <Thead>
                      <Tr>
                        <Th>Source</Th>
                        <Th>Destination (Input JSON)</Th>
                        <Th>Actions</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {step.parameterMappings.map((mapping) => (
                        <Tr key={mapping.id}>
                          <Td>
                            <Select
                              value={mapping.source}
                              onChange={(e) =>
                                handleMappingChange(
                                  mapping.id,
                                  "source",
                                  e.target.value
                                )
                              }
                              size="sm"
                              bg="white"
                            >
                              <option value="">Select source...</option>
                              <optgroup label="Claims">
                                {getAvailableSources()
                                  .filter((s) => s.type === "claim")
                                  .map((source) => (
                                    <option
                                      key={source.value}
                                      value={source.value}
                                    >
                                      {source.label} - {source.details}
                                    </option>
                                  ))}
                              </optgroup>
                              {previousSteps.length > 0 && (
                                <optgroup label="Previous Steps">
                                  {getAvailableSources()
                                    .filter((s) => s.type === "previous_step")
                                    .map((source) => (
                                      <option
                                        key={source.value}
                                        value={source.value}
                                      >
                                        {source.label} - {source.details}
                                      </option>
                                    ))}
                                </optgroup>
                              )}
                              <optgroup label="System Variable">
                                {getAvailableSources()
                                  .filter((s) => s.type === "system_variable")
                                  .map((source) => (
                                    <option
                                      key={source.value}
                                      value={source.value}
                                    >
                                      {source.label} - {source.details}
                                    </option>
                                  ))}
                              </optgroup>
                              <optgroup label="Search Parameters">
                                {getAvailableSources()
                                  .filter((s) => s.type === "Search_parameter")
                                  .map((source) => (
                                    <option
                                      key={source.value}
                                      value={source.value}
                                    >
                                      {source.label} - {source.details}
                                    </option>
                                  ))}
                              </optgroup>
                              <optgroup label="Mail Attachments">
                                {getAvailableSources()
                                  .filter(
                                    (s) =>
                                      s.type === "selected_Mail_Attachments"
                                  )
                                  .map((source) => (
                                    <option
                                      key={source.value}
                                      value={source.value}
                                    >
                                      {source.label} - {source.details}
                                    </option>
                                  ))}
                              </optgroup>
                              <optgroup label="Manual Input">
                                {getAvailableSources()
                                  .filter(
                                    (s) =>
                                      s.type === "manual"
                                  )
                                  .map((source) => (
                                    <option
                                      key={source.value}
                                      value={source.value}
                                    >
                                      {source.label} - {source.details}
                                    </option>
                                  ))}
                              </optgroup>
                            </Select>
                          </Td>
                          <Td>
                            <Select
                              value={mapping.target}
                              onChange={(e) =>
                                handleMappingChange(
                                  mapping.id,
                                  "target",
                                  e.target.value
                                )
                              }
                              size="sm"
                              bg="white"
                            >
                              <option value="">Select target...</option>
                              {getAvailableTargets().map((target) => (
                                <option key={target.value} value={target.value}>
                                  {target.details}
                                </option>
                              ))}
                            </Select>
                          </Td>
                          <Td>
                            <IconButton
                              icon={<Trash2 size={14} />}
                              size="sm"
                              variant="ghost"
                              colorScheme="red"
                              onClick={() =>
                                onRemoveParameterMapping(step.id, mapping.id)
                              }
                              aria-label="Remove mapping"
                            />
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                ) : (
                  <Text
                    fontSize="sm"
                    color="gray.500"
                    textAlign="center"
                    py={4}
                  >
                    No parameter mappings defined yet
                  </Text>
                )}

                <Button
                  leftIcon={<Plus size={14} />}
                  size="sm"
                  variant="outline"
                  mt={2}
                  onClick={() => onAddParameterMapping(step.id)}
                >
                  Add Mapping
                </Button>
              </Box>
            </>
          )}
        </VStack>
      </CardBody>
    </Card>
  );
};

export default StepCard;
